// Reads window.kalidoMewsConfigIds (set in header.php before the SDK loads) and
// builds the Mews standalone deeplink. Shared by BookingForm.jsx and the delegated
// .distributor-open click listener in main.js, so the URL logic lives in one place.

// Encoded id path: each config id encoded, ';' separator preserved (same format the
// embedded widget consumes). Returns '' when no ids are available.
export function buildMewsIdPath() {
  const configIds = (typeof window !== 'undefined' && window.kalidoMewsConfigIds) || '';
  if (!configIds) return '';
  return configIds
    .split(';')
    .map((id) => encodeURIComponent(id.trim()))
    .filter(Boolean)
    .join(';');
}

// Standalone URL. All params optional. start/end are pre-formatted 'YYYY-MM-DD'
// strings (or undefined). Returns null when there are no config ids.
export function buildMewsStandaloneUrl({ start, end, voucherCode } = {}) {
  const idPath = buildMewsIdPath();
  if (!idPath) return null;

  const params = new URLSearchParams();
  if (start) params.set('mewsStart', start);
  if (end) params.set('mewsEnd', end);
  params.set('mewsDistributorOpened', 'true');
  if (voucherCode && voucherCode.trim() !== '') {
    params.set('mewsVoucherCode', voucherCode.trim());
  }
  // Note: the Mews standalone deeplink has no standard guest-count param, so the
  // adult count is intentionally not passed; the user re-selects guests on that page.
  return `https://app.mews.com/distributor/${idPath}?${params.toString()}`;
}

// True only once the Mews widget API is genuinely READY to open the widget (the SDK
// callback in header.php has run and defined kalidoOpenMews). This is the correct gate
// for BOTH the React BookingForm (which calls api methods directly) AND the global
// .distributor-open fallback in main.js: if the API isn't ready, the embedded widget
// cannot open, so the fallback must take over. Note that Mews.Distributor() returning
// without throwing does NOT imply readiness — its async init can fail and never define
// kalidoOpenMews, in which case this correctly stays false and the fallback engages.
export function isMewsSdkReady() {
  return typeof window !== 'undefined' && typeof window.kalidoOpenMews === 'function';
}

// Open the standalone page in a new tab. No-op (returns false) when no config ids.
// Returns true when a tab was opened, so callers can decide whether to preventDefault.
export function openMewsStandalone(params) {
  const url = buildMewsStandaloneUrl(params);
  if (!url) return false;
  window.open(url, '_blank', 'noopener,noreferrer');
  return true;
}
